import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Crown, Utensils, ShoppingCart, Sparkles, Star, MapPin, Phone, Award, Leaf, Heart } from "lucide-react";
import api from "@/lib/api";
import useBranding from "@/hooks/useBranding";
import { resolveImage } from "@/lib/image";

const HERO_DARK = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&q=80";
const HERO_FOOD = "https://images.unsplash.com/photo-1537495988501-f9cd94a78f3e?w=1600&q=80";

export default function Home() {
  const [menu, setMenu] = useState([]);
  const [products, setProducts] = useState([]);
  const { branding } = useBranding();

  useEffect(() => {
    api.get("/menu", { params: { featured: true } })
      .then(({ data }) => setMenu(data.slice(0, 4)))
      .catch(() => {});

    api.get("/products")
      .then(({ data }) => setProducts(data.filter((p) => p.featured || p.fresh_today).slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="grid lg:grid-cols-2 min-h-[85vh]">
        <div
          className="relative chow-theme bg-[#050505] p-8 lg:p-16 flex flex-col justify-center overflow-hidden"
          style={{ backgroundImage: `linear-gradient(rgba(5,5,5,.75), rgba(5,5,5,.92)), url(${HERO_DARK})`, backgroundSize: "cover" }}
        >
          <div className="sun-motif absolute inset-0 opacity-30" />
          <div className="relative z-10 max-w-lg">
            <div className="flex items-center gap-3 mb-6">
              {branding.restaurant_logo ? (
                <img src={resolveImage(branding.restaurant_logo)} alt="ChowMaharlika" className="w-32 h-32 object-contain" />
              ) : (
                <Crown className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
              )}
              <span className="font-accent text-xs text-[#D4AF37]">
                {branding.landing_restaurant_label || "CHOWMAHARLIKA"}
              </span>
            </div>

            <h1 className="font-heading text-5xl lg:text-7xl text-[#FAF9F6] leading-[0.95] mb-4">
              {branding.landing_restaurant_headline_line1 || "Where Filipino Tradition"}
              <br />
              <span className="text-[#D4AF37] italic">
                {branding.landing_restaurant_headline_line2 || "Meets Fresh Quality."}
              </span>
            </h1>

            <p className="text-[#FAF9F6]/70 text-base lg:text-lg mb-8 leading-relaxed">
              {branding.landing_restaurant_description ||
                "Authentic Filipino flavors. Slow-cooked adobo, sizzling sisig, crispy pata and more — made with love, served with pride."}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/restaurant" data-testid="hero-chow-cta" className="btn-chow shimmer">
                {branding.landing_restaurant_primary_cta || "View Menu"}
              </Link>
              <Link to="/lechon" data-testid="hero-lechon-cta" className="btn-chow-outline">
                {branding.landing_restaurant_secondary_cta || "Reserve Lechon"}
              </Link>
            </div>

            <div className="mt-10 flex gap-6 text-xs text-[#FAF9F6]/60 font-accent">
              <span>FAMILY RECIPES</span>
              <span className="text-[#D4AF37]">·</span>
              <span>FRESH DAILY</span>
              <span className="text-[#D4AF37]">·</span>
              <span>DINE · TAKEOUT · CATERING</span>
            </div>
          </div>
        </div>

        <div
          className="bg-[#F2EFEB] p-8 lg:p-16 flex flex-col justify-center relative overflow-hidden"
          style={{ backgroundImage: `linear-gradient(rgba(242,239,235,.92), rgba(242,239,235,.96)), url(${HERO_FOOD})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="max-w-lg">
            <div className="flex items-center gap-3 mb-6">
              {branding.grocery_logo ? (
                <img src={resolveImage(branding.grocery_logo)} alt="Maharlika Mart" className="w-32 h-32 object-contain" />
              ) : (
                <Leaf className="w-7 h-7 text-[#2C4A3B]" strokeWidth={1.5} />
              )}
              <span className="font-accent text-xs text-[#2C4A3B]">
                {branding.landing_grocery_label || "MAHARLIKA MART"}
              </span>
            </div>

            <h2 className="font-heading text-5xl lg:text-6xl text-[#1A2421] leading-[0.95] mb-4">
              {branding.landing_grocery_headline || "Your Filipino grocery destination"}
            </h2>

            <p className="text-[#1A2421]/70 text-base lg:text-lg mb-8 leading-relaxed">
              {branding.landing_grocery_description ||
                "Fresh seafood, daily catches, Filipino pantry staples — quality products at honest prices. Tuloy po kayo!"}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/grocery" data-testid="hero-mart-cta" className="btn-mart inline-flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                {branding.landing_grocery_primary_cta || "Shop Now"}
              </Link>
              <Link to="/promotions" className="px-7 py-3 rounded-full border border-[#2C4A3B] text-[#2C4A3B] hover:bg-[#2C4A3B] hover:text-[#FAF9F6] transition-colors text-sm font-semibold">
                {branding.landing_grocery_secondary_cta || "View Deals"}
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4 text-[#2C4A3B]">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-[#8A9A86] border-2 border-[#F2EFEB]" />
                ))}
              </div>
              <span className="text-sm font-medium">Trusted by Lancaster families</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FAF9F6] py-6 border-y border-[#8A9A86]/20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            ["Fresh Seafood", "Daily market selections"],
            ["Filipino Classics", "Comfort food favorites"],
            ["Party Trays", "For every celebration"],
            ["Grocery Staples", "Pantry essentials"],
          ].map(([title, sub]) => (
            <div key={title}>
              <div className="font-heading text-xl text-[#1A2421]">{title}</div>
              <div className="text-xs text-[#5C6B64]">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-4xl text-[#1A2421]">Featured from the Kitchen</h2>
            <p className="text-[#5C6B64]">Customer favorites from ChowMaharlika.</p>
          </div>
          <Link to="/restaurant" className="hidden md:inline-block text-[#2C4A3B] font-semibold">View all</Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {menu.map((m) => (
            <article key={m.id} className="mart-card overflow-hidden">
              <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(m.image) || HERO_FOOD})` }} />
              <div className="p-4">
                <h3 className="font-heading text-xl text-[#1A2421]">{m.name}</h3>
                <p className="text-sm text-[#5C6B64] line-clamp-2">{m.description}</p>
                <div className="mt-3 text-[#2C4A3B] font-semibold">${Number(m.price || 0).toFixed(2)}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#F2EFEB] py-14">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-4xl text-[#1A2421]">Fresh from the Mart</h2>
              <p className="text-[#5C6B64]">Seafood, grocery staples, and Filipino favorites.</p>
            </div>
            <Link to="/grocery" className="hidden md:inline-block text-[#2C4A3B] font-semibold">Shop all</Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {products.map((p) => (
              <article key={p.id} className="mart-card overflow-hidden bg-[#FAF9F6]">
                <div className="aspect-square bg-cover bg-center bg-[#F2EFEB]" style={{ backgroundImage: `url(${resolveImage(p.image) || ""})` }} />
                <div className="p-4">
                  <h3 className="font-heading text-xl text-[#1A2421]">{p.name}</h3>
                  <p className="text-sm text-[#5C6B64] line-clamp-2">{p.description}</p>
                  <div className="mt-3 text-[#2C4A3B] font-semibold">${Number(p.price || 0).toFixed(2)} / {p.unit}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-3 gap-5">
        <div className="mart-card p-6">
          <MapPin className="w-8 h-8 text-[#2C4A3B] mb-3" />
          <h3 className="font-heading text-2xl text-[#1A2421]">Visit Us</h3>
          <p className="text-[#5C6B64] mt-2">44164 10th St W, Lancaster, CA 93534</p>
        </div>

        <div className="mart-card p-6">
          <Phone className="w-8 h-8 text-[#2C4A3B] mb-3" />
          <h3 className="font-heading text-2xl text-[#1A2421]">Call Ahead</h3>
          <p className="text-[#5C6B64] mt-2">Mart: 661-206-8619<br />Restaurant: 661-206-7487</p>
        </div>

        <div className="mart-card p-6">
          <Heart className="w-8 h-8 text-[#2C4A3B] mb-3" />
          <h3 className="font-heading text-2xl text-[#1A2421]">Made for Community</h3>
          <p className="text-[#5C6B64] mt-2">Groceries, meals, and celebration trays for Filipino families.</p>
        </div>
      </section>
    </div>
  );
}
